import React, { useContext } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { Stack } from '@mui/material';
import { LightbulbOutlined } from '@mui/icons-material';

import { AppContext } from 'context/app';

import { IconLivestream, IconEvents } from 'components/icons';
import { useAspenLogger } from 'hooks/useAspenLogger';
import { EventsNav } from 'layout/navigation/navActions/EventsNav';
import { LivestreamNav } from 'layout/navigation/navActions/LivestreamNav';
import { NavigationDivider } from 'layout/navigation/NavigationDivider';

import styles from './navigation.module.scss';

export const NavigationBar = () => {
  // TODO: Find a way to add trailing slash to default route
  const { pathname } = useLocation();
  const isGridView = /^(\/smarthome)?\/?$/.test(pathname);
  const isFocusedView = /^(\/smarthome)?\/S\w{19}\/?$/.test(pathname);
  const isLivestreamPath = isGridView || isFocusedView;

  const isEventsPath = /^(\/smarthome)?\/events\/?$/.test(pathname);
  const isLightsPath = /^(\/smarthome)?\/lights\/?$/.test(pathname);

  const showNavActions = isLivestreamPath || isEventsPath || isLightsPath;

  // TODO: Store "selected" camera in global state instead of using the first camera
  const { appFeatures } = useContext(AppContext);
  const { logAspenEvent } = useAspenLogger();

  return (
    <Stack className={styles.navBar} data-testid="nav-bar" direction="row" alignItems="center">
      <Stack className={styles.navItems} direction="row" divider={<NavigationDivider />} alignItems="center">
        <Link
          aria-label="livestream-view"
          data-testid="nav-link-livestream-view"
          className={`${styles.navItemLink} ${isLivestreamPath && styles.active}`}
          to="/"
          onClick={() => {
            logAspenEvent({
              log_id: 'iot.web.interaction',
              payload: {
                device_id: '',
                interaction: 'live_view',
              },
            });
          }}
        >
          <Stack className={styles.navItem} direction="row" alignItems="center">
            <IconLivestream />
            Livestream
          </Stack>
        </Link>

        {appFeatures.events && (
          <Link
            aria-label="navigation-events"
            data-testid="nav-link-events"
            className={`${styles.navItemLink} ${isEventsPath && styles.active}`}
            to="/events"
            onClick={() => {
              logAspenEvent({
                log_id: 'iot.web.interaction',
                payload: {
                  device_id: '',
                  interaction: 'events_view',
                },
              });
            }}
          >
            <Stack className={styles.navItem} direction="row" alignItems="center">
              <IconEvents />
              Events
            </Stack>
          </Link>
        )}

        {appFeatures.lights && (
          <Link
            aria-label="navigation-lights"
            data-testid="nav-link-lights"
            className={`${styles.navItemLink} ${isLightsPath && styles.active}`}
            to="/lights"
            onClick={() => {
              logAspenEvent({
                log_id: 'iot.web.interaction',
                payload: {
                  device_id: '',
                  interaction: 'lights_view',
                },
              });
            }}
          >
            <Stack className={styles.navItem} direction="row" alignItems="center">
              <LightbulbOutlined />
              Lights
            </Stack>
          </Link>
        )}
      </Stack>
      {showNavActions && (
        <Stack className={styles.navActions} direction="row" alignItems="center">
          {isLivestreamPath && <LivestreamNav isGridView={isGridView} isFocusedView={isFocusedView} />}
          {isEventsPath && <EventsNav />}
        </Stack>
      )}
    </Stack>
  );
};
